import { useEffect, useMemo } from 'react';

/** ID du conteneur racine du site — utilisé comme scope CSS. */
export const SITE_ROOT_ID = 'proposal-site-root';

/**
 * Génère une feuille de style CSS (string) depuis les `proposalStyles`
 * extraits du `configuration` du proposal.
 *
 * Les règles sont scopées à `#SITE_ROOT_ID` pour ne pas polluer
 * le reste de la page (barre d'outils, modales, etc.).
 */
export function buildProposalCss(proposalStyles) {
  if (!proposalStyles) return '';

  const ff = proposalStyles.fontFamily
    ? `${proposalStyles.fontFamily}, sans-serif`
    : null;
  const fs = proposalStyles.fontSize ? `${proposalStyles.fontSize}px` : null;
  const tc = proposalStyles.baseFontColor || null;

  const titleVarLines = (key, level) => {
    const t = proposalStyles[key];
    if (!t) return '';
    const lines = [];
    // La police du titre passe par la même mécanique que le reste : sans
    // déclaration explicite sur le `h${level}`, c'est la police de titre du
    // thème Mantine qui gagne — une règle qui vise l'élément l'emporte sur la
    // police héritée du bloc, et le titre du site ne ressemblait plus à celui
    // du Proposal.
    const titleFf = t.fontFamily || proposalStyles.fontFamily;
    if (titleFf) lines.push(`  --proposal-h${level}-font-family: ${titleFf}, sans-serif;`);
    if (t.fontSize) lines.push(`  --proposal-h${level}-font-size: ${t.fontSize}px;`);
    if (t.color) lines.push(`  --proposal-h${level}-color: ${t.color};`);
    if (t.fontWeight) lines.push(`  --proposal-h${level}-font-weight: ${t.fontWeight};`);
    if (t.fontStyle) lines.push(`  --proposal-h${level}-font-style: ${t.fontStyle};`);
    if (t.textDecoration) lines.push(`  --proposal-h${level}-text-decoration: ${t.textDecoration};`);
    if (t.marginTop != null) lines.push(`  --proposal-h${level}-margin-top: ${t.marginTop}px;`);
    if (t.marginBottom != null) lines.push(`  --proposal-h${level}-margin-bottom: ${t.marginBottom}px;`);
    if (t.textAlign) lines.push(`  --proposal-h${level}-text-align: ${t.textAlign};`);
    if (t.lineHeight) lines.push(`  --proposal-h${level}-line-height: ${t.lineHeight};`);
    if (t.letterSpacing) lines.push(`  --proposal-h${level}-letter-spacing: ${t.letterSpacing}em;`);
    if (t.backgroundColor && t.backgroundColor !== 'transparent') {
      lines.push(`  --proposal-h${level}-bg-color: ${t.backgroundColor};`);
    }
    return lines.join('\n');
  };

  const headingRule = (level) => {
    const t = proposalStyles[`title${level}`];
    if (!t) return '';
    const decls = [];
    if (t.fontFamily || proposalStyles.fontFamily) {
      decls.push(`font-family: var(--proposal-h${level}-font-family) !important;`);
    }
    if (t.fontSize) decls.push(`font-size: var(--proposal-h${level}-font-size) !important;`);
    if (t.color) decls.push(`color: var(--proposal-h${level}-color) !important;`);
    if (t.fontWeight) decls.push(`font-weight: var(--proposal-h${level}-font-weight) !important;`);
    if (t.fontStyle) decls.push(`font-style: var(--proposal-h${level}-font-style) !important;`);
    if (t.textDecoration) decls.push(`text-decoration: var(--proposal-h${level}-text-decoration) !important;`);
    if (t.marginTop != null) decls.push(`margin-top: var(--proposal-h${level}-margin-top) !important;`);
    if (t.marginBottom != null) decls.push(`margin-bottom: var(--proposal-h${level}-margin-bottom) !important;`);
    // Interligne sans unité et espacement en em : posés aussi sur les
    // descendants, ils se calculent sur la taille de chaque segment — un titre
    // de 108 px écrit dans un span ne prend pas l'espacement de la taille de
    // base du <hN>.
    const spacing = [
      t.lineHeight ? `line-height: var(--proposal-h${level}-line-height) !important;` : '',
      t.letterSpacing ? `letter-spacing: var(--proposal-h${level}-letter-spacing) !important;` : '',
    ].filter(Boolean);
    const rules = [
      ...(decls.length ? [`#${SITE_ROOT_ID} h${level} { ${decls.join(' ')} }`] : []),
      ...(spacing.length ? [`#${SITE_ROOT_ID} h${level}, #${SITE_ROOT_ID} h${level} * { ${spacing.join(' ')} }`] : []),
    ];
    // L'alignement du document n'est qu'un défaut : un titre aligné dans
    // l'éditeur (style inline posé par Tiptap) garde le sien, qu'un
    // `!important` écraserait.
    if (t.textAlign) {
      rules.push(`#${SITE_ROOT_ID} h${level}:not([style*="text-align"]) { text-align: var(--proposal-h${level}-text-align) !important; }`);
    }
    return rules.join('\n');
  };

  const rootVarLines = [
    ff ? `  --proposal-font-family: ${ff};` : '',
    fs ? `  --proposal-base-font-size: ${fs};` : '',
    tc ? `  --proposal-text-color: ${tc};` : '',
    titleVarLines('title1', 1),
    titleVarLines('title2', 2),
    titleVarLines('title3', 3),
    titleVarLines('title4', 4),
  ].filter(Boolean).join('\n');

  // Le texte qui n'a rien de forcé hérite de la racine du site : sans ces
  // déclarations, il héritait du `body` de l'application (Montserrat), que
  // la feuille globale fixe et que Puck recopie dans l'iframe du canvas.
  const rootTextLines = [
    ff ? '  font-family: var(--proposal-font-family);' : '',
    fs ? '  font-size: var(--proposal-base-font-size);' : '',
    tc ? '  color: var(--proposal-text-color);' : '',
  ].filter(Boolean).join('\n');
  const rootLines = [rootVarLines, rootTextLines].filter(Boolean).join('\n');
  const rootBlock = rootLines
    ? `#${SITE_ROOT_ID} {\n${rootLines}\n}`
    : '';

  const headingRules = [1, 2, 3, 4].map(headingRule).filter(Boolean).join('\n');

  return [rootBlock, headingRules].filter(Boolean).join('\n\n');
}

/**
 * Nom de famille exploitable, quelle que soit la forme reçue.
 *
 * `configuration.fontFamily` est une chaîne, mais `configuration.fontFamilyList`
 * est une liste d'objets `{ key, familyName }` (cf. `constants.js`). Passer
 * l'objet tel quel produisait « [object Object] » dans l'URL Google Fonts :
 * toutes les polices secondaires du document étaient demandées sous ce nom, et
 * donc aucune n'était chargée.
 */
export function googleFontName(font) {
  const raw = typeof font === 'string' ? font : font?.familyName || font?.key;
  if (!raw) return '';
  return String(raw).split(',')[0].replace(/["']/g, '').trim();
}

/** Feuille Google Fonts d'une famille, ou '' si le nom est inexploitable. */
export function googleFontHref(font) {
  const family = googleFontName(font);
  if (!family) return '';
  return `https://fonts.googleapis.com/css2?family=${family.replace(/\s+/g, '+')}:wght@400;600;700&display=swap`;
}

/** Feuilles Google Fonts d'une liste de familles, dédoublonnées. */
export function googleFontHrefs(fontFamilies = []) {
  return Array.from(new Set((fontFamilies || []).map(googleFontHref).filter(Boolean)));
}

/**
 * Toutes les polices dont un document a besoin : la police de base, celles de
 * son nuancier, et **celles des quatre niveaux de titre** — un titre peut
 * porter une police que la liste ne cite pas, et elle ne se chargeait alors
 * jamais.
 */
export function proposalFontFamilies(proposalStyles) {
  if (!proposalStyles) return [];
  return [
    proposalStyles.fontFamily,
    ...(proposalStyles.fontFamilyList || []),
    ...[1, 2, 3, 4].map((level) => proposalStyles[`title${level}`]?.fontFamily),
  ].filter(Boolean);
}

/**
 * Injecte dynamiquement un lien Google Fonts dans <head> pour la famille donnée.
 * Ne charge pas deux fois la même police.
 */
export function useGoogleFont(fontFamily) {
  useGoogleFonts(useMemo(() => [fontFamily], [fontFamily]));
}

/**
 * Charge plusieurs polices Google Fonts dans le document courant.
 *
 * Attention : cela ne couvre PAS le canvas de l'éditeur, qui est une iframe
 * avec son propre document — les liens y sont posés par l'override iframe de
 * `PuckPageEditor`.
 */
export function useGoogleFonts(fontFamilies = []) {
  useEffect(() => {
    googleFontHrefs(fontFamilies).forEach((href) => {
      if (document.querySelector(`link[href="${href}"]`)) return;
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      document.head.appendChild(link);
    });
  }, [fontFamilies]);
}
